/**
 * CollectivePageEditor — Annotate a web page snapshot
 *
 * Renders a captured HTML page and lets co-authors:
 * - Select text to suggest edits or add comments
 * - Draw boxes around regions to annotate
 * - Vote on suggestions (same flow as CollectiveDoc)
 * - Guest access via email OTP
 */

import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import DOMPurify from 'dompurify';
const ShareModal = lazy(() => import('./ShareModal.jsx'));

const RegionSelector = lazy(() => import('../RegionSelector.jsx'));

const API = '/api/work/documents';

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

export default function CollectivePageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [doc, setDoc] = useState(null);
  const [snapshotHtml, setSnapshotHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');

  // Guest auth
  const [guestToken, setGuestToken] = useState(() => localStorage.getItem(`guest_token_${id}`) || null);
  const [guestInfo, setGuestInfo] = useState(null);
  const [showGuestLogin, setShowGuestLogin] = useState(false);

  // Share
  const [showShareModal, setShowShareModal] = useState(false);
  const [docVisibility, setDocVisibility] = useState('unlisted');

  // Annotations
  const [suggestions, setSuggestions] = useState([]);
  const [comments, setComments] = useState([]);
  const [sidebarTab, setSidebarTab] = useState('suggestions');
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [pendingRegion, setPendingRegion] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [proposedText, setProposedText] = useState('');
  const [hoveredAnnotation, setHoveredAnnotation] = useState(null);
  const [selectionPopup, setSelectionPopup] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [showCommentInput, setShowCommentInput] = useState(false);

  const contentRef = useRef(null);
  const isOwner = doc && user && doc.user_id === user.id;

  const authHeaders = useCallback(() => {
    const h = {};
    if (guestToken) h['X-Guest-Token'] = guestToken;
    return h;
  }, [guestToken]);

  // Load document
  useEffect(() => {
    (async () => {
      try {
        const headers = authHeaders();
        const res = await fetch(`${API}/${id}`, { credentials: 'include', headers });
        if (res.status === 401 || res.status === 403) {
          if (!isAuthenticated) setShowGuestLogin(true);
          setLoading(false);
          return;
        }
        const data = await res.json();
        setDoc(data.document);
        setTitle(data.document?.title || 'Untitled');
        setSnapshotHtml(data.document?.settings?.snapshotHtml || '');
        setDocVisibility(data.document?.visibility || 'unlisted');
        setLoading(false);
      } catch {
        setLoading(false);
      }
    })();
  }, [id, isAuthenticated, guestToken]);

  // Validate guest session
  useEffect(() => {
    if (!guestToken) return;
    (async () => {
      try {
        const res = await fetch(`/api/docs/${id}/guest/session`, { headers: { 'X-Guest-Token': guestToken } });
        if (res.ok) {
          setGuestInfo(await res.json());
        } else {
          localStorage.removeItem(`guest_token_${id}`);
          setGuestToken(null);
          setShowGuestLogin(true);
        }
      } catch { setShowGuestLogin(true); }
    })();
  }, [guestToken, id]);

  // Load suggestions + comments
  const loadData = useCallback(async () => {
    const headers = authHeaders();
    try {
      const [sugRes, comRes] = await Promise.all([
        fetch(`${API}/${id}/suggestions`, { credentials: 'include', headers }),
        fetch(`${API}/${id}/comments`, { credentials: 'include', headers }),
      ]);
      if (sugRes.ok) setSuggestions((await sugRes.json()).suggestions || []);
      if (comRes.ok) setComments((await comRes.json()).comments || []);
    } catch {}
  }, [id, authHeaders]);

  useEffect(() => { if (doc) loadData(); }, [doc, loadData]);
  useEffect(() => {
    if (!doc) return;
    const iv = setInterval(loadData, 10000);
    return () => clearInterval(iv);
  }, [doc, loadData]);

  // Text selection handler — show floating popup
  useEffect(() => {
    function handleTextSelect() {
      // Small delay to let selection finalize
      setTimeout(() => {
        const selection = window.getSelection();
        const text = selection?.toString()?.trim();
        if (text && text.length > 2 && contentRef.current && selection.rangeCount > 0) {
          // Check if selection is within our content area
          const anchor = selection.anchorNode;
          if (!contentRef.current.contains(anchor)) return;
          setSelectedText(text);
          setProposedText(text);
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const containerRect = contentRef.current.getBoundingClientRect();
          setSelectionPopup({
            top: rect.bottom - containerRect.top + contentRef.current.scrollTop + 4,
            left: Math.min(Math.max(rect.left - containerRect.left, 8), containerRect.width - 220),
          });
          setShowCommentInput(false);
        } else if (!text) {
          setSelectionPopup(null);
          setShowCommentInput(false);
        }
      }, 10);
    }
    document.addEventListener('mouseup', handleTextSelect);
    return () => document.removeEventListener('mouseup', handleTextSelect);
  }, []);

  function openSuggestFromPopup() {
    setShowSuggestModal(true);
    setSelectionPopup(null);
  }

  async function submitComment() {
    if (!commentText.trim() || !selectedText) return;
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    try {
      await fetch(`${API}/${id}/comments`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ content: commentText, anchor_text: selectedText.substring(0, 200) }),
      });
      setCommentText('');
      setShowCommentInput(false);
      setSelectionPopup(null);
      loadData();
      setSidebarTab('comments');
    } catch {}
  }

  // Region capture handler
  function handleRegionCapture({ rect, elements, screenshotDataUrl }) {
    setShowRegionSelector(false);
    setPendingRegion({ rect, elements, screenshotDataUrl });
    setShowNoteModal(true);
  }

  async function submitNote() {
    if (!noteText.trim() || !pendingRegion) return;
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    try {
      await fetch(`${API}/${id}/suggestions`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({
          original_text: pendingRegion.elements.map(e => e.text).filter(Boolean).join(' ').substring(0, 500) || '[Region annotation]',
          proposed_text: noteText,
          anchor_from: Math.round(pendingRegion.rect.x),
          anchor_to: Math.round(pendingRegion.rect.y),
          metadata: {
            type: 'region',
            rect: {
              x: pendingRegion.rect.x,
              y: pendingRegion.rect.y,
              w: pendingRegion.rect.w,
              h: pendingRegion.rect.h,
              width: pendingRegion.rect.w,
              height: pendingRegion.rect.h,
            },
            elementCount: pendingRegion.elements.length,
          },
        }),
      });
      setShowNoteModal(false);
      setNoteText('');
      setPendingRegion(null);
      loadData();
    } catch {}
  }

  async function submitSuggestion() {
    if (!selectedText || !proposedText) return;
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    try {
      await fetch(`${API}/${id}/suggestions`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ original_text: selectedText, proposed_text: proposedText }),
      });
      setShowSuggestModal(false);
      setSelectedText('');
      setProposedText('');
      loadData();
    } catch {}
  }

  async function handleAction(sid, action) {
    await fetch(`${API}/${id}/suggestions/${sid}/${action}`, { method: 'POST', credentials: 'include' });
    loadData();
  }

  async function handleVote(sid, vote) {
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    await fetch(`${API}/${id}/suggestions/${sid}/votes`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({ vote }),
    });
    loadData();
  }

  async function handleUpvote(sid) {
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    await fetch(`${API}/${id}/comments/${sid}/upvote`, { method: 'POST', credentials: 'include', headers });
    loadData();
  }

  function highlightAnnotation(s, switchTab = true) {
    setHoveredAnnotation(s.id);
    // Auto-switch sidebar tab: region annotations → 'suggestions', text comments → 'comments'
    if (switchTab) {
      if (s.metadata?.type === 'region' || s.original_text) setSidebarTab('suggestions');
      else if (s.anchor_text) setSidebarTab('comments');
    }
    if (s.metadata?.type === 'region' && s.metadata?.rect && contentRef.current) {
      const r = s.metadata.rect;
      const wrapper = contentRef.current.closest('.overflow-auto') || contentRef.current.parentElement;
      if (wrapper) wrapper.scrollTo({ top: r.y - 100, behavior: 'smooth' });
      // Flash the highlight
      setTimeout(() => setHoveredAnnotation(null), 3000);
    } else if (s.original_text && contentRef.current) {
      const searchText = s.original_text.substring(0, 100);
      const walker = document.createTreeWalker(contentRef.current, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        const idx = node.textContent.indexOf(searchText.substring(0, 40));
        if (idx >= 0) {
          const range = document.createRange();
          range.setStart(node, idx);
          range.setEnd(node, Math.min(idx + searchText.length, node.textContent.length));
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          node.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
    }
    setTimeout(() => setHoveredAnnotation(null), 3000);
  }

  function handleGuestAuth(token, info) {
    localStorage.setItem(`guest_token_${id}`, token);
    setGuestToken(token);
    setGuestInfo(info);
    setShowGuestLogin(false);
  }

  if (loading) {
    return <div className="flex h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-500" /></div>;
  }

  // Guest login (lazy import the modal from CollectiveDocEditor would be ideal, but inline for now)
  if (showGuestLogin && !isAuthenticated && !guestToken) {
    return <GuestLoginPage docId={id} onAuth={handleGuestAuth} />;
  }

  // Sanitize and scope snapshot CSS so it doesn't bleed into our UI
  const rawSanitized = DOMPurify.sanitize(snapshotHtml, {
    ADD_TAGS: ['style', 'link', 'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g', 'defs', 'use', 'symbol', 'clipPath', 'mask', 'image'],
    ADD_ATTR: ['target', 'rel', 'class', 'style', 'viewBox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'd', 'cx', 'cy', 'r', 'x', 'y', 'width', 'height', 'xmlns', 'transform', 'clip-path', 'color', 'opacity', 'font-size', 'font-weight', 'font-family', 'text-align', 'background', 'background-color', 'border', 'border-radius', 'padding', 'margin', 'display', 'flex', 'gap', 'align-items', 'justify-content'],
    WHOLE_DOCUMENT: false,
    FORCE_BODY: true,
  });
  // Scope all <style> rules inside .snapshot-wrapper so they don't affect sidebar/header
  const sanitizedHtml = rawSanitized.replace(/<style>([\s\S]*?)<\/style>/gi, (match, css) => {
    // Prefix every CSS rule with .snapshot-wrapper
    const scoped = css.replace(/([^{}@]+)\{/g, (ruleMatch, selectors) => {
      if (selectors.trim().startsWith('@')) return ruleMatch; // skip @media etc
      const prefixed = selectors.split(',').map(s => `.snapshot-wrapper ${s.trim()}`).join(', ');
      return `${prefixed} {`;
    });
    return `<style>${scoped}</style>`;
  });

  return (
    <div className="flex h-screen flex-col bg-gray-100">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-4 py-2 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate('/work')} className="rounded p-1.5 text-gray-400 hover:bg-gray-100 flex-shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          </div>
          <span className="text-base sm:text-lg font-semibold text-gray-900 truncate">{title}</span>
          <span className="text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">Page</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setShowRegionSelector(true)}
            className="px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100">
            Draw Region
          </button>
          <button onClick={() => setShowShareModal(true)}
            className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
            Share
          </button>
          {isOwner && doc?.settings?.sourceUrl && (
            <button onClick={async () => {
              try {
                const res = await fetch(`${API}/${id}/re-snapshot`, { method: 'POST', credentials: 'include' });
                if (res.ok) window.location.reload();
              } catch {}
            }}
              className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Re-import page with latest styles">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
          )}
          {doc?.settings?.sourceUrl && (
            <a href={doc.settings.sourceUrl} target="_blank" rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-gray-600 hidden sm:inline truncate max-w-[200px]">
              {new URL(doc.settings.sourceUrl).hostname}
            </a>
          )}
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Page content */}
        <div className="flex-1 overflow-auto bg-white" ref={contentRef}>
          <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 snapshot-wrapper relative">
            <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
            {/* Region annotation overlays & pins */}
            {suggestions.filter(s => s.metadata?.type === 'region' && s.metadata?.rect).map(s => {
              const r = s.metadata.rect;
              // RegionSelector stores { x, y, w, h } (screen coords)
              // Convert to container-relative coords
              const containerRect = contentRef.current?.getBoundingClientRect();
              const scrollTop = contentRef.current?.scrollTop || 0;
              const rLeft = (r.x || 0) - (containerRect?.left || 0);
              const rTop = (r.y || 0) - (containerRect?.top || 0) + scrollTop;
              const rWidth = r.w || r.width || 100;
              const rHeight = r.h || r.height || 50;
              const isHovered = hoveredAnnotation === s.id;
              return (
                <div key={`region-${s.id}`}>
                  {/* Highlight overlay */}
                  <div data-region={s.id} style={{ position: 'absolute', left: rLeft, top: rTop, width: rWidth, height: rHeight, pointerEvents: 'none',
                    border: isHovered ? '2px solid #14b8a6' : '2px dashed rgba(20, 184, 166, 0.5)',
                    backgroundColor: isHovered ? 'rgba(20, 184, 166, 0.15)' : 'rgba(20, 184, 166, 0.08)',
                    borderRadius: 4, transition: 'all 0.15s ease', zIndex: 10,
                  }} />
                  {/* Pin indicator at top-right */}
                  <div
                    onMouseEnter={() => setHoveredAnnotation(s.id)}
                    onMouseLeave={() => setHoveredAnnotation(null)}
                    onClick={() => {
                      setHoveredAnnotation(s.id);
                      setSidebarTab('suggestions');
                      // Scroll sidebar to this annotation
                      setTimeout(() => {
                        document.querySelector(`[data-sidebar-id="${s.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }}
                    style={{ position: 'absolute', left: r.x + r.width - 12, top: r.y - 8, zIndex: 20, cursor: 'pointer' }}
                    title={s.proposed_text?.substring(0, 80)}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shadow-md border-2 transition-all ${isHovered ? 'bg-teal-500 border-white scale-125' : 'bg-teal-400 border-white'}`}>
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selection popup */}
          {selectionPopup && (
            <div style={{ position: 'absolute', top: selectionPopup.top, left: selectionPopup.left, zIndex: 30 }}>
              {showCommentInput ? (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3 w-64">
                  <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-teal-500 outline-none resize-none"
                    rows={2} autoFocus />
                  <div className="flex justify-end gap-1.5 mt-2">
                    <button onClick={() => { setShowCommentInput(false); setCommentText(''); }}
                      className="px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
                    <button onClick={submitComment} disabled={!commentText.trim()}
                      className="px-2.5 py-1 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">Comment</button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900 rounded-lg shadow-lg flex overflow-hidden">
                  <button onClick={openSuggestFromPopup}
                    className="px-3 py-2 text-xs font-medium text-white hover:bg-gray-700 flex items-center gap-1.5 border-r border-gray-700">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    Suggest Edit
                  </button>
                  <button onClick={() => setShowCommentInput(true)}
                    className="px-3 py-2 text-xs font-medium text-white hover:bg-gray-700 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                    Comment
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="hidden sm:flex w-72 border-l border-gray-200 bg-white flex-col flex-shrink-0">
          <div className="flex border-b border-gray-100">
            <button onClick={() => setSidebarTab('suggestions')}
              className={`flex-1 px-3 py-2.5 text-xs font-medium ${sidebarTab === 'suggestions' ? 'text-teal-700 border-b-2 border-teal-500' : 'text-gray-500'}`}>
              Annotations ({suggestions.length})
            </button>
            <button onClick={() => setSidebarTab('comments')}
              className={`flex-1 px-3 py-2.5 text-xs font-medium ${sidebarTab === 'comments' ? 'text-teal-700 border-b-2 border-teal-500' : 'text-gray-500'}`}>
              Comments ({comments.length})
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {sidebarTab === 'suggestions' ? (
              suggestions.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-8">Select text or draw a region to annotate.</p>
              ) : (
                suggestions.map(s => (
                  <div key={s.id} data-sidebar-id={s.id}
                    className={`rounded-lg p-3 border transition-all cursor-pointer ${hoveredAnnotation === s.id ? 'bg-teal-50 border-teal-300 ring-1 ring-teal-200' : 'bg-gray-50 border-gray-100'}`}
                    onMouseEnter={() => setHoveredAnnotation(s.id)}
                    onMouseLeave={() => setHoveredAnnotation(null)}
                    onClick={() => highlightAnnotation(s)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">{s.author_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        s.status === 'pending' ? 'bg-amber-100 text-amber-700' : s.status === 'voting' ? 'bg-blue-100 text-blue-700' : s.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>{s.status}</span>
                    </div>
                    {s.metadata?.type === 'region' ? (
                      <div className="mb-1.5">
                        {s.proposed_text && <p className="text-xs text-gray-600">{s.proposed_text}</p>}
                      </div>
                    ) : (
                      <div className="text-xs mb-1.5">
                        <span className="line-through text-red-600 bg-red-50 px-1 rounded">{s.original_text?.substring(0, 60)}</span>
                        <span className="mx-1 text-gray-300">→</span>
                        <span className="text-green-700 bg-green-50 px-1 rounded">{s.proposed_text?.substring(0, 60)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex gap-1">
                        {s.status === 'pending' && isOwner && (
                          <>
                            <button onClick={e => { e.stopPropagation(); handleAction(s.id, 'accept'); }} className="px-2 py-1 text-[10px] font-medium text-green-700 bg-green-50 rounded hover:bg-green-100">Accept</button>
                            <button onClick={e => { e.stopPropagation(); handleAction(s.id, 'reject'); }} className="px-2 py-1 text-[10px] font-medium text-red-700 bg-red-50 rounded hover:bg-red-100">Reject</button>
                            <button onClick={e => { e.stopPropagation(); handleAction(s.id, 'move-to-vote'); }} className="px-2 py-1 text-[10px] font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100">Vote</button>
                          </>
                        )}
                        {s.status === 'voting' && (
                          <>
                            <button onClick={e => { e.stopPropagation(); handleVote(s.id, 'yes'); }} className="px-2 py-1 text-[10px] font-medium text-green-700 bg-green-50 rounded hover:bg-green-100">Yes</button>
                            <button onClick={e => { e.stopPropagation(); handleVote(s.id, 'no'); }} className="px-2 py-1 text-[10px] font-medium text-red-700 bg-red-50 rounded hover:bg-red-100">No</button>
                            <button onClick={e => { e.stopPropagation(); handleVote(s.id, 'needs_discussion'); }} className="px-2 py-1 text-[10px] font-medium text-amber-700 bg-amber-50 rounded hover:bg-amber-100">Discuss</button>
                          </>
                        )}
                      </div>
                      {/* Upvote */}
                      <button onClick={e => { e.stopPropagation(); handleVote(s.id, 'yes'); }}
                        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-teal-600 transition-colors" title="Upvote">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        {(Number(s.yes_count) || 0) > 0 && <span>{s.yes_count}</span>}
                      </button>
                    </div>
                  </div>
                ))
              )
            ) : (
              comments.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-8">No comments yet.</p>
              ) : (
                comments.map(c => (
                  <div key={c.id} className={`rounded-lg p-3 border cursor-pointer transition-all ${hoveredAnnotation === c.id ? 'bg-teal-50 border-teal-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}
                    onMouseEnter={() => setHoveredAnnotation(c.id)}
                    onMouseLeave={() => setHoveredAnnotation(null)}
                    onClick={() => {
                      setHoveredAnnotation(c.id);
                      if (c.anchor_text && contentRef.current) {
                        const walker = document.createTreeWalker(contentRef.current, NodeFilter.SHOW_TEXT);
                        let node;
                        while ((node = walker.nextNode())) {
                          const idx = node.textContent.indexOf(c.anchor_text.substring(0, 40));
                          if (idx >= 0) {
                            const range = document.createRange();
                            range.setStart(node, idx);
                            range.setEnd(node, Math.min(idx + c.anchor_text.length, node.textContent.length));
                            const sel = window.getSelection();
                            sel.removeAllRanges();
                            sel.addRange(range);
                            node.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            setTimeout(() => setHoveredAnnotation(null), 3000);
                            break;
                          }
                        }
                      }
                      setHoveredAnnotation(c.id);
                      setTimeout(() => setHoveredAnnotation(null), 3000);
                    }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">{c.display_name}</span>
                      <span className="text-[10px] text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    {c.anchor_text && (
                      <div className="text-[10px] text-teal-600 bg-teal-50 px-2 py-1 rounded mb-1.5 truncate">"{c.anchor_text}"</div>
                    )}
                    <p className="text-xs text-gray-600">{c.content}</p>
                  </div>
                ))
              )
            )}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <Suspense fallback={null}>
          <ShareModal docId={id} currentVisibility={docVisibility} isOwner={isOwner}
            onClose={() => setShowShareModal(false)} onUpdate={v => setDocVisibility(v)} />
        </Suspense>
      )}

      {/* Region Selector */}
      {showRegionSelector && (
        <Suspense fallback={null}>
          <RegionSelector
            container={contentRef.current}
            onCapture={handleRegionCapture}
            onCancel={() => setShowRegionSelector(false)}
          />
        </Suspense>
      )}

      {/* Note Modal (after region draw) */}
      {showNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowNoteModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Add Note to Region</h3>
            {pendingRegion?.screenshotDataUrl && (
              <img src={pendingRegion.screenshotDataUrl} alt="Selected region" className="w-full rounded-lg border border-gray-200 mb-3 max-h-40 object-contain bg-gray-50" />
            )}
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Describe the issue or suggestion..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-1 focus:ring-teal-500 outline-none min-h-[80px]" autoFocus />
            <div className="flex gap-2 justify-end mt-3">
              <button onClick={() => setShowNoteModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitNote} className="px-4 py-2 text-sm bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700">Add Note</button>
            </div>
          </div>
        </div>
      )}

      {/* Suggest Edit Modal (text selection) */}
      {showSuggestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSuggestModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Suggest Edit</h3>
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Original text</label>
              <div className="bg-red-50 text-red-800 text-sm rounded-lg p-3 line-through">{selectedText}</div>
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Proposed replacement</label>
              <textarea value={proposedText} onChange={e => setProposedText(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-1 focus:ring-teal-500 outline-none min-h-[80px]" autoFocus />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSuggestModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitSuggestion} className="px-4 py-2 text-sm bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700">Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Simple guest login (reuses the pattern from CollectiveDocEditor)
function GuestLoginPage({ docId, onAuth }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [agreedContrib, setAgreedContrib] = useState(false);

  async function requestOtp() {
    if (!name || !email || !agreed || !agreedContrib) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/docs/${docId}/guest/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, display_name: name }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setStep(2);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }

  async function verifyOtp() {
    if (!code) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/docs/${docId}/guest/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      const data = await res.json();
      onAuth(data.token, data);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Join as Guest</h2>
          <p className="text-sm text-gray-500 mt-1">Verify your email to annotate this page</p>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">{error}</div>}
        {step === 1 ? (
          <div className="space-y-3">
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-teal-500" />
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-teal-500" />
            <label className="flex items-start gap-2 text-xs text-gray-500 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 rounded" />
              <span>I agree to the <a href="/terms" target="_blank" className="text-teal-600 underline">Terms</a> and <a href="/privacy" target="_blank" className="text-teal-600 underline">Privacy Policy</a></span>
            </label>
            <label className="flex items-start gap-2 text-xs text-gray-500 cursor-pointer">
              <input type="checkbox" checked={agreedContrib} onChange={e => setAgreedContrib(e.target.checked)} className="mt-0.5 rounded" />
              <span>I understand my contributions are tracked but cannot be redeemed until I register as an official contributor.</span>
            </label>
            <button onClick={requestOtp} disabled={loading || !name || !email || !agreed || !agreedContrib}
              className="w-full py-2.5 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm">
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 text-center">Code sent to <strong>{email}</strong></p>
            <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-3 text-center text-2xl font-mono tracking-[0.5em] outline-none focus:ring-1 focus:ring-teal-500" />
            <button onClick={verifyOtp} disabled={loading || code.length < 6}
              className="w-full py-2.5 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm">
              {loading ? 'Verifying...' : 'Verify & Join'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
